import { formatAmount } from '../../utils/orderDisplay'

import { Modal } from '@/components/ui/modal'
import { SelectField } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'
import { toast as message } from 'sonner'
import { StatusBadge, Notice } from '../products/PageHelpers'
import { useEffect, useState } from 'react'

import {
  getProviderDispute,
  listProviderDisputes,
} from '../../services/providerDisputes'
import type {
  DisputeCaseType,
  DisputeStatus,
  MerchantDisputeDetail,
  MerchantProviderDispute,
} from '../../types/providerDispute'
import { formatTimestamp } from '../../utils/format'

const statusLabels: Record<DisputeStatus, string> = {
  NEEDS_RESPONSE: '待响应',
  UNDER_REVIEW: '抗辩材料已提交',
  WON: '商户胜诉',
  LOST: '商户败诉',
  ACCEPTED: '已接受',
  CANCELED: '已取消',
  CLOSED: '已关闭',
  UNKNOWN: '未知',
}
const statusColors: Record<DisputeStatus, string> = {
  NEEDS_RESPONSE: 'orange',
  UNDER_REVIEW: 'blue',
  WON: 'green',
  LOST: 'red',
  ACCEPTED: 'purple',
  CANCELED: 'default',
  CLOSED: 'default',
  UNKNOWN: 'default',
}
const caseTypeLabels: Record<DisputeCaseType, string> = {
  RETRIEVAL_REQUEST: '信息请求',
  CHARGEBACK: '拒付',
  RDR: 'RDR',
  COMPLIANCE_REQUEST: '合规请求',
  OTHER: '其他',
}
function money(value?: string, currency?: string) {
  if (value == null || !currency) return '-'
  return `${formatAmount(value)} ${currency}`
}

export default function DisputeList() {
  const [items, setItems] = useState<MerchantProviderDispute[]>([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState<DisputeStatus | undefined>()
  const [loading, setLoading] = useState(false)
  const [detail, setDetail] = useState<MerchantDisputeDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  useEffect(() => {
    setLoading(true)
    listProviderDisputes({ page, size: pageSize, status })
      .then((response) => {
        setItems(response.data.items)
        setTotal(response.data.pagination.total)
      })
      .catch((error) => message.error(error.message))
      .finally(() => setLoading(false))
  }, [page, pageSize, status])
  const openDetail = (record: MerchantProviderDispute) => {
    setDetailLoading(true)
    getProviderDispute(record.id)
      .then((response) => setDetail(response.data))
      .catch((error) => message.error(error.message))
      .finally(() => setDetailLoading(false))
  }
  const columns = [
    {
      key: 'providerDisputeId',
      header: '案件号',
      render: (row: MerchantProviderDispute) => {
        const value = row['providerDisputeId']
        return value || '-'
      },
    },
    {
      key: 'orderId',
      header: '订单',
      render: (row: MerchantProviderDispute) => {
        const value = row['orderId']
        return value || '-'
      },
    },
    {
      key: 'caseType',
      header: '类型',
      render: (row: MerchantProviderDispute) => {
        const value = row['caseType']
        return caseTypeLabels[value]
      },
    },
    {
      key: 'amount',
      header: '金额',
      className: 'text-right tabular-nums',
      render: (row: MerchantProviderDispute) => {
        const record = row
        return money(record.amount, record.currency)
      },
    },
    {
      key: 'status',
      header: '状态',
      render: (row: MerchantProviderDispute) => {
        const value = row['status']
        return (
          <StatusBadge color={statusColors[value]}>
            {statusLabels[value]}
          </StatusBadge>
        )
      },
    },
    {
      key: 'occurredAt',
      header: '发生时间',
      className: 'text-xs text-muted-foreground',
      render: (row: MerchantProviderDispute) => {
        const value = row['occurredAt']
        return formatTimestamp(value)
      },
    },
    {
      key: 'action',
      header: '操作',
      className: 'whitespace-nowrap',
      render: (row: MerchantProviderDispute) => {
        const record = row
        return (
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => openDetail(record)}
          >
            查看详情
          </button>
        )
      },
    },
  ]
  return (
    <div className="page-container">
      <div style={{ width: '100%' }} className={'flex flex-col gap-3'}>
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold tracking-tight">争议案件</h2>
          <section role="search" aria-label="争议案件筛选" className="flex min-w-0 max-w-full flex-wrap items-center gap-2">
            <label htmlFor="merchant-dispute-status" className="text-xs text-muted-foreground">状态</label>
            <div className="w-[180px] max-w-full">
              <SelectField
                id="merchant-dispute-status"
                aria-label="状态"
                options={[
                  { value: '', label: '全部' },
                  ...Object.entries(statusLabels).map(([value, label]) => ({ value, label })),
                ]}
                placeholder="状态"
                value={status}
                onValueChange={(value) => {
                  setPage(1)
                  setStatus((value || undefined) as DisputeStatus | undefined)
                }}
              />
            </div>
          </section>
        </div>
        <DataTable
          rowKey={'id'}
          loading={loading}
          columns={columns}
          data={items}
          pagination={{
            page: page,
            size: pageSize,
            total,
            onChange: (next, size) => {
              setPage(next)
              setPageSize(size)
            },
          }}
        />
      </div>
      <Modal
        title={'争议案件详情'}
        open={Boolean(detail)}
        onOpenChange={(open) => {
          if (!open) (() => setDetail(null))()
        }}
      >
        {detailLoading && <p role="status">載入中…</p>}
        {detail && (
          <div style={{ width: '100%' }} className={'flex flex-col gap-3'}>
            <h2 className="mb-4 text-xl font-semibold tracking-tight">
              {detail.summary.providerDisputeId || detail.summary.id}
            </h2>
            <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'上游争议金额'}
                </dt>
                <dd className="break-words text-sm">
                  {money(detail.summary.amount, detail.summary.currency)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'状态'}</dt>
                <dd className="break-words text-sm">
                  <StatusBadge color={statusColors[detail.summary.status]}>
                    {statusLabels[detail.summary.status]}
                  </StatusBadge>
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'订单'}</dt>
                <dd className="break-words text-sm">
                  {detail.summary.orderId || '-'}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'订阅'}</dt>
                <dd className="break-words text-sm">
                  {detail.summary.subscriptionId || '-'}/{' '}
                  {detail.summary.subscriptionInvoiceId || '-'}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'响应截止'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(detail.summary.responseDueAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">
                  {'发生时间'}
                </dt>
                <dd className="break-words text-sm">
                  {formatTimestamp(detail.summary.occurredAt)}
                </dd>
              </div>
              <div className="min-w-0 bg-white p-3">
                <dt className="mb-1 text-xs text-muted-foreground">{'原因'}</dt>
                <dd className="break-words text-sm">
                  {detail.summary.reason || '-'}
                </dd>
              </div>
            </dl>
            <div>
              <h2 className="mb-4 text-xl font-semibold tracking-tight">
                资金影响
              </h2>
              <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border bg-border md:grid-cols-2">
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'结算币种'}
                  </dt>
                  <dd className="break-words text-sm">
                    {detail.summary.settlementCurrency || '-'}
                  </dd>
                </div>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'实际冻结本金'}
                  </dt>
                  <dd className="break-words text-sm">
                    {money(
                      detail.summary.frozenPrincipal,
                      detail.summary.settlementCurrency,
                    )}
                  </dd>
                </div>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'关联滚动准备金'}
                  </dt>
                  <dd className="break-words text-sm">
                    {money(
                      detail.summary.allocatedRollingReserve,
                      detail.summary.settlementCurrency,
                    )}
                  </dd>
                </div>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'已扣本金'}
                  </dt>
                  <dd className="break-words text-sm">
                    {money(
                      detail.summary.chargedBackPrincipal,
                      detail.summary.settlementCurrency,
                    )}
                  </dd>
                </div>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'已抵扣滚动准备金'}
                  </dt>
                  <dd className="break-words text-sm">
                    {money(
                      detail.summary.offsetRollingReserve,
                      detail.summary.settlementCurrency,
                    )}
                  </dd>
                </div>
                <div className="min-w-0 bg-white p-3">
                  <dt className="mb-1 text-xs text-muted-foreground">
                    {'拒付费'}
                  </dt>
                  <dd className="break-words text-sm">
                    {money(
                      detail.summary.chargebackFee,
                      detail.summary.settlementCurrency,
                    )}
                  </dd>
                </div>
              </dl>
            </div>
            {detail.summary.relatedCaseCount > 1 && (
              <Notice
                tone={'info'}
                title={`此订单有 ${detail.summary.relatedCaseCount} 个关联争议案件，资金影响在案件间共享。`}
              />
            )}
            <h2 className="mb-4 text-xl font-semibold tracking-tight">
              案件时间线
            </h2>
            <ol className="divide-y divide-border border-l pl-5">
              {detail.timeline.map((event, index) => (
                <li key={index} className="min-h-14 space-y-1 py-3 text-sm">
                  <div className="font-medium">{event.eventType} · {statusLabels[event.status]}</div>
                  <div className="text-xs text-muted-foreground">{formatTimestamp(event.occurredAt || event.createdAt)}</div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </Modal>
    </div>
  )
}
